/** Family Card application letter — data helpers. */

export const RECIPIENT_VALUES = [
    'ward_member',
    'chairman',
    'union_chairman',
    'ward_representative',
];

export const INITIAL_FAMILY_CARD_FORM = {
    recipientType: 'ward_member',
    unionName: '',
    applicantName: '',
    fatherName: '',
    motherName: '',
    nid: '',
    mobile: '',
    village: '',
    postOffice: '',
    wardNo: '',
    upazila: '',
    district: '',
    sonsCount: '',
    daughtersCount: '',
};

export function displayField(value) {
    const v = String(value ?? '').trim();
    return v || '';
}

/** Convert Bengali/Arabic-Indic digits to ASCII — used only for validation logic. */
function toAsciiDigits(str) {
    return str
        .replace(/[\u09E6-\u09EF]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x09e6 + 48))
        .replace(/[\u0660-\u0669]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48));
}

/**
 * Keep only digit characters (ASCII 0-9, Bengali ০-৯, Arabic-Indic ٠-٩).
 */
export function normalizeDigits(value, maxLength) {
    const digits = String(value ?? '').replace(/[^\d\u09E6-\u09EF\u0660-\u0669]/g, '');
    return maxLength ? digits.slice(0, maxLength) : digits;
}

/** Strip to digits, drop leading 880 country code if present. */
export function normalizeBdMobile(value) {
    let digits = String(value ?? '').replace(/[^\d\u09E6-\u09EF\u0660-\u0669]/g, '');
    const ascii = toAsciiDigits(digits);
    if (ascii.startsWith('880') && ascii.length >= 13) {
        digits = digits.slice(3);
    }
    return digits.slice(0, 11);
}

/** Bangladesh mobile: 01[3-9]xxxxxxxx */
export function isValidBdMobile(value) {
    const ascii = toAsciiDigits(normalizeBdMobile(value));
    return /^01[3-9]\d{8}$/.test(ascii);
}

const FIELD_HISTORY_KEY = 'family-card-form-field-history';
const FORM_SNAPSHOT_KEY = 'family-card-form-last-snapshot';
const FIELD_HISTORY_MAX = 8;

const REQUIRED_FIELDS = [
    'unionName',
    'applicantName',
    'fatherName',
    'motherName',
    'nid',
    'mobile',
    'village',
    'postOffice',
    'wardNo',
    'upazila',
    'district',
    'sonsCount',
    'daughtersCount',
];

/** @returns {string[]} Previously entered values for a field (newest first). */
export function getFamilyCardFieldHistory(fieldName) {
    if (typeof window === 'undefined' || !fieldName) return [];
    try {
        const store = JSON.parse(localStorage.getItem(FIELD_HISTORY_KEY) || '{}');
        const list = store[fieldName];
        return Array.isArray(list) ? list.filter((v) => typeof v === 'string' && v.trim()) : [];
    } catch {
        return [];
    }
}

/** @returns {typeof INITIAL_FAMILY_CARD_FORM | null} */
export function getFamilyCardFormSnapshot() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(FORM_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return { ...INITIAL_FAMILY_CARD_FORM, ...parsed };
    } catch {
        return null;
    }
}

/** Save the latest complete application for quick restore / suggestions. */
export function saveFamilyCardFormSnapshot(form) {
    if (typeof window === 'undefined' || !form) return;
    try {
        const payload = {
            recipientType: form.recipientType,
            unionName: displayField(form.unionName),
            applicantName: displayField(form.applicantName),
            fatherName: displayField(form.fatherName),
            motherName: displayField(form.motherName),
            nid: displayField(form.nid),
            mobile: displayField(form.mobile),
            village: displayField(form.village),
            postOffice: displayField(form.postOffice),
            wardNo: displayField(form.wardNo),
            upazila: displayField(form.upazila),
            district: displayField(form.district),
            sonsCount: displayField(form.sonsCount),
            daughtersCount: displayField(form.daughtersCount),
        };
        localStorage.setItem(FORM_SNAPSHOT_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

/** Suggestions for a field: saved history + snapshot. */
export function getFamilyCardFieldSuggestions(fieldName) {
    const history = getFamilyCardFieldHistory(fieldName);
    const snapshot = getFamilyCardFormSnapshot();
    const fromSnapshot = snapshot ? displayField(snapshot[fieldName]) : '';
    return [...new Set([...history, fromSnapshot].filter(Boolean))];
}

/** Remember a non-empty value for browser-style suggestions. */
export function saveFamilyCardFieldHistory(fieldName, value) {
    if (typeof window === 'undefined' || !fieldName) return;
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return;

    try {
        const store = JSON.parse(localStorage.getItem(FIELD_HISTORY_KEY) || '{}');
        const prev = Array.isArray(store[fieldName]) ? store[fieldName] : [];
        const next = [trimmed, ...prev.filter((v) => v !== trimmed)].slice(0, FIELD_HISTORY_MAX);
        store[fieldName] = next;
        localStorage.setItem(FIELD_HISTORY_KEY, JSON.stringify(store));
    } catch {
        /* ignore quota / private mode */
    }
}

/**
 * @param {typeof INITIAL_FAMILY_CARD_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {{ field: string, message: string }[]}
 */
export function getFamilyCardFormValidationErrors(form, t) {
    const errors = [];

    for (const key of REQUIRED_FIELDS) {
        if (key === 'mobile') continue;
        if (!displayField(form[key])) {
            errors.push({
                field: key,
                message: t('familyCardForm.validation.required', {
                    field: t(`familyCardForm.fields.${key}`),
                }),
            });
        }
    }

    const mobile = displayField(form.mobile);
    if (!mobile) {
        errors.push({ field: 'mobile', message: t('familyCardForm.validation.mobileRequired') });
    } else {
        const mobileErr = getMobileValidationError(form.mobile, t);
        if (mobileErr) {
            errors.push({ field: 'mobile', message: mobileErr });
        }
    }

    return errors;
}

export function isFamilyCardFormComplete(form, t) {
    return getFamilyCardFormValidationErrors(form, t).length === 0;
}

/**
 * Every blank still empty, named the way the form asks for it and in the order
 * it asks — what the download confirmation lists back to the applicant. The
 * recipient is a select that always holds a choice, so it is never listed.
 *
 * @param {typeof INITIAL_FAMILY_CARD_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {string[]}
 */
export function getEmptyFamilyCardFields(form, t) {
    return Object.keys(INITIAL_FAMILY_CARD_FORM)
        .filter((key) => key !== 'recipientType' && !displayField(form?.[key]))
        .map((key) => t(`familyCardForm.fields.${key}`));
}

export function getMobileValidationError(value, t) {
    const digits = normalizeBdMobile(value);
    if (!digits) {
        return '';
    }
    if (digits.length < 11) {
        return t('familyCardForm.validation.mobileLength', { count: digits.length });
    }
    if (!isValidBdMobile(digits)) {
        return t('familyCardForm.validation.mobileInvalid');
    }
    return '';
}

export function buildUnionDisplay(form, t) {
    const name = displayField(form.unionName);
    return name || t('familyCardForm.preview.unionPlaceholder');
}

export function formatChildCountInline(value) {
    const v = displayField(value);
    return v || '......';
}

export function buildFamilyCardLetter(form, recipientLabel, t) {
    return {
        recipient: recipientLabel,
        unionDisplay: buildUnionDisplay(form, t),
        applicantName: displayField(form.applicantName),
        fatherName: displayField(form.fatherName),
        motherName: displayField(form.motherName),
        nid: displayField(form.nid),
        mobile: displayField(form.mobile),
        village: displayField(form.village),
        postOffice: displayField(form.postOffice),
        wardNo: displayField(form.wardNo),
        upazila: displayField(form.upazila),
        district: displayField(form.district),
        sonsCount: formatChildCountInline(form.sonsCount),
        daughtersCount: formatChildCountInline(form.daughtersCount),
    };
}

/** @param {{ field: string, message: string }[]} errors */
export function validationErrorsToFieldMap(errors) {
    const map = {};
    for (const { field, message } of errors) {
        map[field] = message;
    }
    return map;
}
